import { useEffect, useRef, useCallback } from 'react'
import { AppState, AppStateStatus } from 'react-native'

interface UseBackgroundTaskOptions {
    interval: number // en millisegundos
    runOnMount?: boolean
    runOnAppFocus?: boolean
    enabled?: boolean
}

/**
 * Hook personalizado para ejecutar tareas en segundo plano de forma confiable
 * @param task - Función async que se ejecutará periódicamente
 * @param options - Configuración del comportamiento
 */
export const useBackgroundTask = (
    task: () => Promise<void> | void,
    options: UseBackgroundTaskOptions
) => {
    const {
        interval,
        runOnMount = true,
        runOnAppFocus = true,
        enabled = true
    } = options

    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const isActiveRef = useRef(true)
    const appStateRef = useRef(AppState.currentState)

    const executeTask = useCallback(async () => {
        if (!isActiveRef.current || !enabled) return

        try {
            await task()
        } catch (error) {
            console.error('❌ Error en tarea de segundo plano:', error)
        }
    }, [task, enabled])

    const startInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        intervalRef.current = setInterval(() => {
            if (isActiveRef.current && enabled) {
                executeTask()
            }
        }, interval)

    }, [interval, executeTask, enabled])

    const stopInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    // Manejar cambios en el estado de la app
    useEffect(() => {
        if (!runOnAppFocus) return

        const handleAppStateChange = (nextAppState: AppStateStatus) => {
            if (appStateRef.current.match(/inactive|background/) && nextAppState === 'active') {
                // console.log('📱 App volvió al primer plano, ejecutando tarea')
                executeTask()
            }
            appStateRef.current = nextAppState
        }

        const subscription = AppState.addEventListener('change', handleAppStateChange)

        return () => {
            subscription?.remove()
        }
    }, [executeTask, runOnAppFocus])

    // Efecto principal
    useEffect(() => {
        if (!enabled) {
            stopInterval()
            return
        }

        isActiveRef.current = true

        // Ejecutar al montar si está habilitado
        if (runOnMount) {
            executeTask()
        }

        // Iniciar intervalo
        startInterval()

        return () => {
            isActiveRef.current = false
            stopInterval()
        }
    }, [enabled, runOnMount, startInterval, stopInterval, executeTask])

    return {
        executeNow: executeTask,
        start: startInterval,
        stop: stopInterval,
        isRunning: intervalRef.current !== null
    }
}
